import cx from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import {
  dataTypes,
  AlloyButton,
  Table,
  columnTypes,
  ShoppingCartIcon
} from 'cat-ecommerce-alloy';
import { useSelector, useDispatch } from 'react-redux';
import ProductLink from '../../../../../common/product/ProductLink';
import useAddToCart from '../../../../../../hooks/useAddToCart';
import { activateHighlight, deactivateHighlight } from './utils';
import { useTranslation } from 'react-i18next';
import { STATUS } from '../../../../../../constants/commonConstants';
import {
  clearError,
  setError
} from '../../../../../../store/exception/actions';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  FEEDBACK_TYPES
} from '../../../../../../constants/errorConstants';
import Availability from '../../../../../common/Availability';
import GstTableHeaderDisclaimer from '../../../../../common/product/GstTableHeaderDisclaimer';
import AddToCartActions from '../../../../../common/product/ProductPrimaryActions/AddToCartActions/AddToCartActions';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import ContactDealerButtonPopup from '../../../../../common/ContactDealerButtonPopup';
import styles from '../../styles.module.scss';
import { normalizeError } from '../../../../../../store/exception/utils';
import {
  getFormattedUtcDatestamp,
  replaceTokensInString
} from '../../../../../../utils';
import ProductPriceDetails from './ProductPriceDetails';
import { FITMENT_TRUE } from '@app/constants/analyticsConstants';
import {
  DealerLocatorModalWrapper,
  CheckPriceDescription as CheckPriceDescriptionBlocks
} from '@app/components/dealer-locator-react';
import { useModal } from '@cat-ecom/pcc-components';
import { PCC_RELEASE_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';

const ProductSchematicsPartsList = ({
  parts = [],
  partsInformation = [],
  priceData,
  priceCallStatus,
  priceCallError,
  schematicsRef,
  selected
}) => {
  const PCC_PDP_DIAGRAM = 'PCC PDP Diagram';

  const { fireProductImpressionEvent } = useAnalytics();
  const dispatch = useDispatch();
  const { addToCart, error: addToCartError } = useAddToCart();
  const [t] = useTranslation();

  const didProductImpressionEventFire = useRef(false);
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const currency = useSelector(s => s.dealerCustomer?.currency ?? 'USD');

  const isPriceCallResolved = priceCallStatus === STATUS.RESOLVED;
  const isPriceCallLoading = priceCallStatus === STATUS.PENDING;
  const isPriceCallRejected = priceCallStatus === STATUS.REJECTED;

  const priceAndAvailability = priceData ?? [];
  const taxDisclaimerMessage = useSelector(
    s => s.dealerCustomer?.taxDisclaimerMessage
  );
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const relatedModel = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const locale = useSelector(s => s.common?.locale);
  const esiteURL = useSelector(s => s.common?.esiteURL);
  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag?.[PCC_RELEASE_HEADLESS_PDP_FLAG]) ?? false;

  const currentProduct = useSelector(s => s.products.current);
  const getProductInfoForGA = useCallback(
    (product, index) => ({
      availability: product?.availability,
      name: `${product?.partNumber}: ${product?.name}`,
      partNumber: product?.partNumber,
      brand: product?.manufacturer,
      category: product?.categoryPath,
      quantity: product?.quantity,
      price: product?.unformattedUnitPrice,
      businessUnit: 'PCC',
      productWeight: product?.weight,
      list: PCC_PDP_DIAGRAM,
      position: index + 1,
      checkFitment: FITMENT_TRUE,
      relatedModel
    }),
    [relatedModel]
  );

  const modal = useModal({
    isRequired: false,
    heading: t('PDP_SHOW_PRICE_AVAILABILITY_SCHEMATIC_SECTION'),
    backButton: false,
    size: 'md'
  });

  const isValidPrice = part =>
    part.discountedPrice && part.discountedPrice !== formattedZeroPrice;
  const isReplacedPart = part =>
    part.indirectReplacement ||
    (Array.isArray(part.replacementParts) && part.replacementParts.length > 0);

  const shouldShowPriceAndAddToCart = part =>
    isPriceCallResolved &&
    isValidPrice(part) &&
    !isReplacedPart(part) &&
    !isCatCorp;

  const purchaseInfoLabel = (
    <>
      {replaceTokensInString(t('PRICE_CODE_AVAILABILITY'), currency)}
      {!!taxDisclaimerMessage && (
        <p className="mt-1">
          <GstTableHeaderDisclaimer
            className="fw-normal"
            taxDisclaimerMessage={taxDisclaimerMessage}
          />
        </p>
      )}
    </>
  );

  const addToCartLabel = <div className="me-2">{t('ADD_TO_CART')}</div>;

  const schema = [
    {
      id: 'referenceNumberTag',
      dataType: dataTypes.NUMBER,
      width: '38px',
      className: 'align-top text-md'
    },
    {
      id: 'productLink',
      label: t('PART_NAME'),
      hideOnMobile: true,
      className: 'align-top text-sm',
      dataType: dataTypes.ALPHANUMERIC,
      columnType: columnTypes.STRETCHED
    },
    {
      id: 'purchaseDetails',
      label: purchaseInfoLabel,
      dataType: dataTypes.ALPHANUMERIC,
      columnType: columnTypes.STRETCHED,
      hideOnMobile: true,
      cellProperties: isCatCorp ? { colSpan: '2' } : {},
      className: 'align-top text-sm'
    },
    {
      id: 'productLinkAndDetailsMobile',
      hideOnDesktop: true,
      className: 'align-top text-sm'
    },
    {
      id: 'productCheckPriceMobile',
      hideOnDesktop: true,
      className: 'align-top text-sm'
    },
    {
      id: 'cta',
      padding: '0px 4px 0px 0px',
      label: addToCartLabel,
      dataType: dataTypes.ICON,
      columnType: columnTypes.FIXED,
      width: '80px',
      className: isCatCorp ? 'align-top d-none text-sm' : 'align-top text-sm'
    }
  ];

  const data = parts.map(part => ({
    ...part,
    ...partsInformation[part.uniqueId],
    ...priceAndAvailability[part.partNumber],
    rowProperties:
      currentProduct === part.uniqueId
        ? activateHighlight(schematicsRef, part.referenceNumber, false, true)
        : {
            onMouseEnter: () =>
              activateHighlight(schematicsRef, part.referenceNumber),
            onMouseLeave: () => deactivateHighlight(schematicsRef, part),
            onFocus: () =>
              activateHighlight(schematicsRef, part.referenceNumber, true),
            onBlur: () => deactivateHighlight(schematicsRef, part)
          },
    reactKey: part.uniqueId
  }));

  useEffect(() => {
    didProductImpressionEventFire.current = false;
  }, [selected]);

  useEffect(() => {
    if (
      (isCatCorp || isPriceCallResolved || isPriceCallRejected) &&
      !didProductImpressionEventFire.current
    ) {
      const products = data.map((product, index) =>
        getProductInfoForGA(product, index)
      );

      fireProductImpressionEvent(PCC_PDP_DIAGRAM, products);
      didProductImpressionEventFire.current = true;
    }
  }, [
    data,
    fireProductImpressionEvent,
    getProductInfoForGA,
    isCatCorp,
    isPriceCallRejected,
    isPriceCallResolved
  ]);

  const handleAddToCart = useCallback(
    part => {
      dispatch(clearError(ERROR_DOMAIN.PDP, ERROR_PATH.SCHEMATICS));
      addToCart(
        [{ quantity: `${part.quantity}`, productId: part.itemCatEntryId }],
        { x_calculateOrder: part.sosCode ?? '' }
      );
    },
    [addToCart, dispatch]
  );

  useEffect(() => {
    if (addToCartError) {
      dispatch(
        setError(ERROR_DOMAIN.PDP, ERROR_PATH.SCHEMATICS, {
          message: 'UNKNOWN_ERROR_MESSAGE',
          title: 'UNKNOWN_ERROR_TITLE'
        })
      );
    }
    if (priceCallError) {
      const priceCallErrorProps = {
        message: `${t('PA_TIMEOUT')} ${getFormattedUtcDatestamp(locale)}`,
        severity: FEEDBACK_TYPES.ERROR,
        title: 'GENERIC_ERROR'
      };

      dispatch(
        setError(ERROR_DOMAIN.PDP, ERROR_PATH.SCHEMATICS, {
          ...normalizeError(addToCartError, priceCallErrorProps, true)
        })
      );
    }
  }, [dispatch, addToCartError, locale, priceCallError, t]);

  const addToCartIcon = (
    <ShoppingCartIcon
      style={{ fill: '#FFFFFF' }}
      className={styles['btn__icon']}
    />
  );
  const cta = (part, col, footer, row) =>
    shouldShowPriceAndAddToCart(part) ? (
      <AddToCartActions
        addToCartIcon={addToCartIcon}
        addToCartClassName={styles['btn--icon-only']}
        onAddToCart={() => handleAddToCart(part)}
        quantity={part.quantity}
        showSmallVariant={true}
        gaListName={PCC_PDP_DIAGRAM}
        productClickDetails={getProductInfoForGA(part, row)}
      />
    ) : null;

  const referenceNumberTag = part => (
    <div
      className={cx(
        'px-0 text-center text-md',
        currentProduct === part.uniqueId
          ? styles['currentPartHighlight']
          : styles['partHighlight']
      )}
      name={`ID_${part.referenceNumber}_part`}
    >
      <span>{part.referenceNumber}</span>
      {currentProduct !== part.uniqueId && (
        <span className={cx(styles['referenceTagParentheses'])}>{')'}</span>
      )}
    </div>
  );

  const getProductLink = (productDisplayUrl, partNumber) => {
    const urlBasedonFeatureFlag = isPDPHeadlessFeatureFlagEnable
      ? `${esiteURL}/product/${partNumber}`
      : productDisplayUrl;
    return urlBasedonFeatureFlag;
  };

  const productLink = (part, col, footer, row, className) =>
    part.productDisplayUrl ? (
      <ProductLink
        link={getProductLink(part.productDisplayUrl, part.partNumber)}
        className={cx('text-univers text-md text-capitalize', className)}
        gaListName={PCC_PDP_DIAGRAM}
        productClickDetails={getProductInfoForGA(part, row)}
      >
        {`${part.partNumber}: ${part.name.toLowerCase()}`}
      </ProductLink>
    ) : (
      <span
        className={cx('text-univers text-md text-capitalize', className)}
      >{`${part.partNumber}: ${part.name.toLowerCase()}`}</span>
    );

  const handleCheckPrice = () => {
    didProductImpressionEventFire.current = false;
    modal.initiatorProps.onClick();
  };

  const getCheckPriceButton = rowIndex =>
    rowIndex === 0 ? (
      <AlloyButton
        buttonType="primary"
        className="mt-2 mb-2 m-lg-0 py-2"
        onClick={handleCheckPrice}
        size="small"
      >
        {t('PDP_SHOW_PRICE_AVAILABILITY_SCHEMATIC_SECTION')}
      </AlloyButton>
    ) : null;

  const getContactUsDealerButton = (part, rowIndex) =>
    rowIndex === 0 ? (
      <ContactDealerButtonPopup catEntryId={part.itemCatEntryId} />
    ) : null;

  const getPriceAndAvailability = (part, rowIndex, showCurrency = true) => (
    <>
      {shouldShowPriceAndAddToCart(part) ? (
        <ProductPriceDetails
          currency={currency}
          price={part.discountedPrice}
          unitPrice={part.unitPrice}
          showCurrency={showCurrency}
        />
      ) : null}
      {isPriceCallRejected ? (
        getContactUsDealerButton(part, rowIndex)
      ) : (
        <Availability
          availabilityInformation={part.availability}
          catEntryId={part.itemCatEntryId}
          hideLabel={true}
          isLoading={isPriceCallLoading}
        />
      )}
    </>
  );

  const purchaseDetails = (part, col, footer, rowIndex) =>
    isCatCorp && rowIndex === 0 ? (
      getCheckPriceButton(rowIndex)
    ) : (
      <div className={cx('d-flex flex-column')}>
        {getPriceAndAvailability(part, rowIndex, false)}
      </div>
    );

  const productLinkAndDetailsMobile = item => (
    <div className="d-flex">
      <div className={cx('d-flex flex-column')}>
        {productLink(item, null, null, null, 'mt-1 mb-2')}
        {getPriceAndAvailability(item, null, true)}
      </div>
    </div>
  );

  const productCheckPriceMobile = (_item, _col, _isFooter, rowIndex) => {
    return isCatCorp ? getCheckPriceButton(rowIndex) : null;
  };

  const getCustomRenderers = () => ({
    referenceNumberTag,
    cta,
    ctaMobile: cta,
    productLink,
    purchaseDetails,
    productLinkAndDetailsMobile,
    productCheckPriceMobile
  });

  return (
    <div className={styles['diagram__table-container']}>
      <Table
        data={data}
        schema={schema}
        customRenderers={getCustomRenderers()}
        className="text-md"
      />
      <DealerLocatorModalWrapper modal={modal}>
        <CheckPriceDescriptionBlocks />
      </DealerLocatorModalWrapper>
    </div>
  );
};

ProductSchematicsPartsList.propTypes = {
  parts: PropTypes.arrayOf(
    PropTypes.shape({
      partNumber: PropTypes.string,
      quantity: PropTypes.number,
      referenceNumber: PropTypes.string,
      uniqueId: PropTypes.string
    })
  ),
  partsInformation: PropTypes.shape({
    itemCatEntryId: PropTypes.string,
    name: PropTypes.string,
    parentCatEntryId: PropTypes.string,
    partNumber: PropTypes.string,
    productDisplayUrl: PropTypes.string,
    uniqueId: PropTypes.string
  }),
  schematicsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  selected: PropTypes.string,
  priceData: PropTypes.shape([{}]),
  priceCallStatus: PropTypes.string,
  priceCallError: PropTypes.shape({})
};

export default ProductSchematicsPartsList;
